<script setup>
import { computed } from 'vue'

const props = defineProps({
  name: { type: String, default: '' },
  size: { type: Number, default: 40 },
  // Availability badge: 'online' | 'offline' | null
  status: { type: String, default: null },
  // Uploaded photo; operators fall back to the company logo, clients to initials
  src: { type: String, default: null },
  logoFallback: { type: Boolean, default: false },
})

const DEFAULT_LOGO = '/default-avatar.png'

const imageSrc = computed(
  () => props.src || (props.logoFallback ? DEFAULT_LOGO : null)
)

const STATUS_CLASSES = {
  online: 'bg-n-teal-10',
  busy: 'bg-n-amber-10',
  offline: 'bg-n-slate-10',
}

const initials = computed(() => {
  const parts = props.name.trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
})

const PALETTE = [
  'bg-n-iris-9',
  'bg-n-blue-9',
  'bg-n-teal-9',
  'bg-n-amber-9',
  'bg-n-ruby-9',
]

const colorClass = computed(() => {
  const sum = props.name
    .split('')
    .reduce((acc, ch) => acc + ch.charCodeAt(0), 0)
  return PALETTE[sum % PALETTE.length]
})

const containerStyle = computed(() => ({
  width: `${props.size}px`,
  height: `${props.size}px`,
}))

const circleStyle = computed(() => ({
  fontSize: `${Math.round(props.size / 2.6)}px`,
}))

// 35% of the avatar size (min 8px), anchored to the bottom-right corner.
const badgeStyle = computed(() => {
  const badgeSize = Math.max(props.size * 0.35, 8)
  return {
    width: `${badgeSize}px`,
    height: `${badgeSize}px`,
    top: `${props.size - badgeSize / 1.1}px`,
    left: `${props.size - badgeSize / 1.1}px`,
  }
})
</script>

<template>
  <span
    class="relative inline-flex shrink-0 align-middle"
    :style="containerStyle"
  >
    <span
      v-if="status && STATUS_CLASSES[status]"
      class="absolute z-20 border rounded-full border-n-slate-3"
      :style="badgeStyle"
      :class="STATUS_CLASSES[status]"
    />
    <img
      v-if="imageSrc"
      :src="imageSrc"
      :alt="name"
      class="object-cover w-full h-full rounded-full"
    />
    <span
      v-else
      class="inline-flex items-center justify-center w-full h-full rounded-full font-medium text-white"
      :class="colorClass"
      :style="circleStyle"
    >
      {{ initials }}
    </span>
  </span>
</template>
